import { Avatar } from '@seek/avatar';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Text,
  Link,
  TextLink,
  IconHistory,
  Inline,
  ButtonIcon,
  IconDocument,
  IconMail,
  IconNote,
  IconResume,
  Bleed,
} from 'braid-design-system';
import type { FC } from 'react';

import { TimeAgo } from 'src/components/common';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { Display } from 'src/libs/Display';

import { StatusBadge } from '../StatusBadge';
import { TableRowLayout } from '../TableLayout';
import type { YourCandidatesNode_CandidateRowFragment } from '../YourCandidates/YourCandidates.generated';
import type { YourCandidateStatus } from '../graphql/hirer-types.generated';

import translations from './.vocab';
import { MostRecentRoleMobile } from './MostRecentRoleMobile';

export const statusQueryStringMap: Record<
  YourCandidateStatus,
  string | undefined
> = {
  NEW: undefined,
  VIEWED: undefined,
  PRESCREEN: 'Prescreen',
  SHORTLIST: 'Shortlist',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  ACCEPT: 'Accept',
  REJECTED: 'Rejected',
};

export interface CandidateRowProps {
  candidate: YourCandidatesNode_CandidateRowFragment;
  onClickCandidateName: () => void;
  onClickPreviousApplication: () => void;
  onClickCoverLetterDownload: () => void;
  onClickResumeDownload: () => void;
  onClickSelectionCriteriaDownload: () => void;
  onClickEmailCandidate: () => void;
}

export const CandidateRow: FC<CandidateRowProps> = ({
  candidate: {
    name,
    phoneNumber,
    channel,
    status,
    lastRoleJobTitle,
    lastRoleCompanyName,
    jobTitle,
    totalApplications,
    hasCoverLetter,
    hasResume,
    hasSelectionCriteria,
    jobId,
    prospectId,
    applicationTime,
  },
  onClickCandidateName,
  onClickPreviousApplication,
  onClickCoverLetterDownload,
  onClickResumeDownload,
  onClickSelectionCriteriaDownload,
  onClickEmailCandidate,
}) => {
  const { t } = useTranslations(translations);
  const { router, locale } = useAppConfig();

  const statusQueryString = statusQueryStringMap[status];

  const displayedTotalApplications = totalApplications - 1;
  const shouldShowPrevAppsOnMobile = displayedTotalApplications > 1;

  const renderPreviousApplications = ({ mobile }: { mobile: boolean }) => {
    if (mobile && !shouldShowPrevAppsOnMobile) {
      return null;
    }

    if (displayedTotalApplications <= 0) {
      return (
        <Text size="small" align={['left', 'right']}>
          0
        </Text>
      );
    }

    return (
      <Text size="small" maxLines={2} align={['left', 'right']}>
        <TextLink
          weight="weak"
          href={router.candidates({
            jobid: jobId,
            selected: prospectId.toString(),
            tab: JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS,
            s: statusQueryString,
          })}
          onClick={onClickPreviousApplication}
        >
          {mobile ? (
            <>
              <IconHistory />{' '}
            </>
          ) : null}
          {t('previousApplicationsCount', {
            previousApplicationsCount: displayedTotalApplications,
          })}
        </TextLink>
      </Text>
    );
  };

  return (
    <TableRowLayout
      avatar={
        <>
          <Display mobile>
            <Avatar variant="initials" name={name} size="standard" />
          </Display>
          <Display tablet desktop>
            <Avatar variant="initials" name={name} size="large" />
          </Display>
        </>
      }
      name={
        <Text weight="strong" size="small" maxLines={2}>
          <Link
            href={router.candidates({
              jobid: jobId,
              selected: prospectId.toString(),
              s: statusQueryString,
            })}
            onClick={onClickCandidateName}
          >
            {name}
          </Link>
        </Text>
      }
      manuallyAdded={
        channel === 'UPLOADED' ? (
          <Badge tone="neutral">{t('Manually added')}</Badge>
        ) : null
      }
      phoneNumber={
        phoneNumber ? (
          <Text size="small" maxLines={1}>
            <TextLink href={`tel:${phoneNumber}`} weight="weak">
              {phoneNumber}
            </TextLink>
          </Text>
        ) : null
      }
      status={<StatusBadge status={status} />}
      mostRecentRole={
        lastRoleJobTitle ? (
          <Text size="small" maxLines={1}>
            {lastRoleJobTitle}
          </Text>
        ) : (
          <Text size="small">{t('New to workforce')}</Text>
        )
      }
      mostRecentCompanyName={
        lastRoleJobTitle && lastRoleCompanyName ? (
          <Text size="small" maxLines={1}>
            {lastRoleCompanyName}
          </Text>
        ) : null
      }
      mostRecentRoleMobile={
        <MostRecentRoleMobile
          mostRecentRoleCompanyName={lastRoleCompanyName}
          mostRecentRoleTitle={lastRoleJobTitle}
        />
      }
      mostRecentApplication={
        <>
          <Display mobile>
            <Text size="small" maxLines={2}>
              {t('Applied for {jobTitle}', { jobTitle })}
            </Text>
          </Display>
          <Display tablet desktop>
            <Text size="small" maxLines={1}>
              {jobTitle}
            </Text>
          </Display>
        </>
      }
      relativeApplicationTime={
        <Text size="small">
          <TimeAgo date={applicationTime} locale={locale} />
        </Text>
      }
      previousApplications={renderPreviousApplications({ mobile: false })}
      actions={
        <Bleed
          left={[shouldShowPrevAppsOnMobile ? 'none' : 'xsmall', 'none']}
          right={['none', 'xsmall']}
        >
          <Inline
            space={['xsmall', 'none']}
            alignY="center"
            align={['left', 'right']}
          >
            {shouldShowPrevAppsOnMobile ? (
              <Display mobile>
                {renderPreviousApplications({ mobile: true })}
              </Display>
            ) : null}
            {hasCoverLetter ? (
              <ButtonIcon
                id={`${prospectId}-download-cover-letter-btn`}
                label={t('Download cover letter')}
                icon={<IconDocument />}
                variant="transparent"
                bleed={false}
                onClick={onClickCoverLetterDownload}
              />
            ) : null}
            {hasResume ? (
              <ButtonIcon
                id={`${prospectId}-download-resume-btn`}
                label={t('Download resumé')}
                icon={<IconResume />}
                variant="transparent"
                bleed={false}
                onClick={onClickResumeDownload}
              />
            ) : null}
            {hasSelectionCriteria ? (
              <ButtonIcon
                id={`${prospectId}-download-selection-criteria-btn`}
                label={t('Download selection criteria')}
                icon={<IconNote />}
                variant="transparent"
                bleed={false}
                onClick={onClickSelectionCriteriaDownload}
              />
            ) : null}
            <ButtonIcon
              id={`${prospectId}-email-btn`}
              label={t('Email applicant')}
              icon={<IconMail />}
              variant="transparent"
              bleed={false}
              onClick={onClickEmailCandidate}
            />
          </Inline>
        </Bleed>
      }
    />
  );
};
